import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard,
  UploadCloud,
  History,
  FileText,
  CreditCard,
  LogOut,
  Shield,
  Settings,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/upload", label: "New Analysis", icon: UploadCloud, testid: "nav-upload" },
  { to: "/uploads", label: "Upload History", icon: History, testid: "nav-uploads" },
  { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
  { to: "/billing", label: "Access & Plans", icon: CreditCard, testid: "nav-billing" },
  { to: "/account", label: "Account", icon: Settings, testid: "nav-account" },
];

const ADMIN_NAV = { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" };

export function DashboardLayout({ children, title, subtitle, action }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    // Navigate to the public intro/signup page FIRST (replace history so Back can't
    // return to a protected page), THEN clear the session. Ordering matters: clearing
    // auth while still on a protected route would trigger the guard redirect to /login.
    navigate("/signup", { replace: true });
    await logout();
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[#050914] text-slate-100 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-800 bg-[#0A1124] fixed inset-y-0 left-0 z-20">
        <div className="px-6 py-6 border-b border-slate-800">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {[...NAV, ...(user?.role === "admin" ? [ADMIN_NAV] : [])].map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9 border border-slate-700">
              <AvatarFallback className="bg-slate-800 text-cyan-300 text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">
                {user?.name || "User"}
              </p>
              <p className="truncate text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="mt-2 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-slate-800 bg-[#0A1124]/90 backdrop-blur-xl px-4 py-3">
          <Logo />
          <button
            onClick={handleLogout}
            data-testid="logout-btn-mobile"
            className="flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>

        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight text-white" data-testid="page-title">
                {title}
              </h1>
              {subtitle && <p className="mt-1 text-slate-400">{subtitle}</p>}
            </div>
            {action}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
